import { useEffect, useRef, useState, useCallback } from 'react';

interface SensorUpdate {
  device_id: string;
  timestamp: string;
  sensors: {
    temperature?: { value: number; unit: string };
    humidity?: { value: number; unit: string };
    air_quality?: { value: number; unit: string };
  };
}

interface SSEStatus {
  connected: boolean;
  reconnecting: boolean;
  error: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

export const useSensorSSE = (
  onUpdate: (data: SensorUpdate) => void,
  deviceId?: string
) => {
  const [status, setStatus] = useState<SSEStatus>({
    connected: false,
    reconnecting: false,
    error: null,
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      console.log('[SSE] Connecting to sensor updates stream...');
      const eventSource = new EventSource(`${API_URL}/events/sensor-updates`);
      eventSourceRef.current = eventSource;

      // Connection established
      eventSource.addEventListener('connected', (event) => {
        console.log('[SSE] Connected:', event.data);
        setStatus({ connected: true, reconnecting: false, error: null });
      });

      // Heartbeat to keep connection alive
      eventSource.addEventListener('heartbeat', () => {
        console.log('[SSE] Heartbeat received');
      });

      // Sensor update event
      eventSource.addEventListener('sensor_update', (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[SSE] Sensor update received:', data);

          // Filter by device ID if specified
          if (!deviceId || data.device_id === deviceId) {
            onUpdate(data.data);
          }
        } catch (error) {
          console.error('[SSE] Error parsing sensor update:', error);
        }
      });

      // Connection opened
      eventSource.onopen = () => {
        console.log('[SSE] Connection opened');
        setStatus({ connected: true, reconnecting: false, error: null });
      };

      // Error handling
      eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);
        setStatus({
          connected: false,
          reconnecting: true,
          error: 'Connection lost',
        });

        // Close and attempt reconnect after 3 seconds
        eventSource.close();
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[SSE] Attempting to reconnect...');
          connect();
        }, 3000);
      };
    } catch (error) {
      console.error('[SSE] Error creating EventSource:', error);
      setStatus({
        connected: false,
        reconnecting: false,
        error: 'Failed to establish connection',
      });
    }
  }, [deviceId, onUpdate]);

  // Initialize connection on mount
  useEffect(() => {
    connect();

    // Cleanup on unmount
    return () => {
      console.log('[SSE] Cleaning up connection');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return { status, reconnect: connect };
};
